import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Eye, EyeOff, Shield } from "lucide-react";
import BrandText from "../components/BrandText";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(formData.email, formData.password);
      // Always show welcome overlay for demo users on every login
      if (user.plan_type === "demo") {
        Object.keys(localStorage).forEach(k => { if (k.startsWith("demo_welcome_seen_")) localStorage.removeItem(k); });
      }
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate("/dashboard");
    } catch (error) {
      const message = extractErrorMessage(error, "Invalid credentials");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: "linear-gradient(135deg, #F8FAFC 0%, #FFF7ED 100%)" }}
      data-testid="login-page"
    >
      <div className="w-full max-w-[420px]">
        {/* Login Card */}
        <div className="bg-white border border-slate-200 rounded-xl p-10 shadow-sm" data-testid="login-card">
          {/* Logo */}
          <div className="mb-1">
            <BrandText size="xl" className="block" />
          </div>
          <p className="text-xs text-slate-400 mb-8">AI-Powered Resiliency Management Service</p>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <label className="block text-[0.6875rem] font-semibold text-slate-500 mb-1.5">Email</label>
            <div className="relative mb-4">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                placeholder="bcmadmin@yourorg.com"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-10 pr-4 text-[0.8125rem] text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-sky-600 focus:ring-[3px] focus:ring-sky-600/12"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="login-email-input"
              />
            </div>

            {/* Password */}
            <label className="block text-[0.6875rem] font-semibold text-slate-500 mb-1.5">Password</label>
            <div className="relative mb-6">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg py-2.5 pl-10 pr-10 text-[0.8125rem] text-slate-900 placeholder:text-slate-400 outline-none transition-all focus:border-sky-600 focus:ring-[3px] focus:ring-sky-600/12"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-sky-700 hover:bg-sky-800 text-white font-bold text-[0.8125rem] py-2.5 rounded-lg transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            {/* Request Demo Outline Button */}
            <Link to="/request-demo" data-testid="login-register-link">
              <button
                type="button"
                className="w-full mt-2.5 bg-transparent border border-slate-200 text-slate-500 hover:border-sky-600 hover:text-sky-700 font-bold text-[0.8125rem] py-2.5 rounded-lg transition-colors"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Request Enquiry &amp; Demo
              </button>
            </Link>
          </form>

          {/* Forgot Password */}
          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-xs text-sky-700 hover:text-sky-800 font-medium" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          {/* ISO Compliance Footer */}
          <p className="text-[0.6875rem] text-slate-400 text-center mt-6 flex items-center justify-center gap-1.5">
            <Shield className="w-3 h-3" />
            ISO 22301:2019 Compliant Platform
          </p>
        </div>
      </div>
    </div>
  );
}
